import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import { Button } from '../../../../../components/common/buttons/Button';
import { Icon } from '../../../../../components/common/icon/Icon';
import { MarketPriceFilterOptionValueSorterEnum } from '../../../../../constants/filter/marketPriceFilter.constant';
import {
  filterSliderConstant,
  priceSliderValueMapping,
  PriceSliderValueMappingType,
} from '../../../../../constants/filter/slider/filterSliderConstant';
import { marketPricePageInfo } from '../../../../../constants/pageInfo/pageInfo.constant';
import { useGetMachinePriceStatistics } from '../../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatistics.query';
import { useGetMachinePriceStatisticsById } from '../../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatisticsById.query';
import { IProduct } from '../../../../../interfaces/Product.interfaces';

interface ProductMarketPriceSectionProps {
  product: IProduct;
  category: 'PLUVA' | 'PARTNER';
}

export const ProductMarketPriceSection = ({ product, category }: ProductMarketPriceSectionProps) => {
  const navigate = useNavigate();

  const isPluvaProduct = category === 'PLUVA';
  const isTractor = product.machine.machineType === 'tractor';
  const {
    id: machineId,
    model: modelName,
    brand: { id: brandId, name: brandName },
  } = product.machine;

  const { data: machinePriceStatistic, isLoading: isMachinePriceStatisticLoading } = useGetMachinePriceStatisticsById({
    machineId: machineId as string,
    options: {
      enabled: isTractor,
    },
  });

  const hasSameModelMarketPriceData: boolean = Boolean(machinePriceStatistic);

  const { data: machinePriceList } = useGetMachinePriceStatistics({
    searchParam: {
      brand: brandId,
      minEngineOutputPs: filterSliderConstant.engineOutputInPs.min,
      maxEngineOutputPs: filterSliderConstant.engineOutputInPs.max,
      minMedianPrice: filterSliderConstant.medianPrice.min,
      maxMedianPrice: priceSliderValueMapping[filterSliderConstant.medianPrice.max as PriceSliderValueMappingType],
      sort: MarketPriceFilterOptionValueSorterEnum.default_desc,
      page: 1,
      count: 5,
    },
    options: {
      enabled: !isMachinePriceStatisticLoading && !hasSameModelMarketPriceData,
    },
  });

  const hasSameBrandMarketPriceData: boolean = (machinePriceList && machinePriceList?.pages[0]?.length > 0) ?? false;

  if (isPluvaProduct && isTractor && (hasSameModelMarketPriceData || hasSameBrandMarketPriceData)) {
    return (
      <Container>
        <TractorPricePromotionSection>
          <PromoTitleSection>
            <Icon name={'CircleWon'} width={24} height={24} fill={'#FF9500'} />
            <PromoTitle>
              {hasSameModelMarketPriceData && `${modelName}, 적정 가격인지 궁금하신가요?`}
              {!hasSameModelMarketPriceData && `${brandName} 시세는 얼마일까?`}
            </PromoTitle>
          </PromoTitleSection>
          <PromoDescription>{`${'‘트랙터 시세’에서 현재 시세를 확인해보세요.'}`}</PromoDescription>
          <ButtonWrapper>
            <Button
              borderType={'round'}
              color={'filledGreyLight'}
              size={'sm'}
              text={{
                size: 'md',
                content: `${hasSameModelMarketPriceData ? modelName : brandName} 시세 확인하기`,
              }}
              icon={{ fileName: 'AngleRight', size: 'Small', position: 'right', fill: '#505363' }}
              onClick={() => {
                ampli.pvmClickPdpToMarketPriceSearchResultButton({
                  title: product.title,
                });
                navigate(
                  `${marketPricePageInfo.searchResultPage.path}?keyword=${brandName} ${
                    hasSameModelMarketPriceData ? modelName : ''
                  }`,
                );
              }}
            />
          </ButtonWrapper>
        </TractorPricePromotionSection>
      </Container>
    );
  }

  return null;
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding: 0px var(--radius-large, 16px) 40px var(--radius-large, 16px);

  gap: 40px;
`;

const TractorPricePromotionSection = styled.div`
  display: flex;
  padding: 32px 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PromoTitleSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const PromoTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const PromoTitleHighlight = styled.span`
  color: var(--Light-Scheme-Color-primary-orange, #ff9500);
`;

const PromoDescription = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonWrapper = styled.div``;
